import { useEffect, useRef, useState } from "react";

import { fetchMessage, type Message } from "./messages-api";

export type DetailStatus = "loading" | "ok" | "not-found" | "forbidden" | "error";

export interface UseMessageDetailResult {
  status: DetailStatus;
  message: Message | null;
  error: string | null;
}

export function useMessageDetail(
  baseUrl: string,
  namespaceId: string,
  messageId: string
): UseMessageDetailResult {
  const [status, setStatus] = useState<DetailStatus>("loading");
  const [message, setMessage] = useState<Message | null>(null);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setStatus("loading");
    setMessage(null);
    setError(null);

    void (async () => {
      try {
        const result = await fetchMessage(baseUrl, namespaceId, messageId);
        if (requestId !== requestIdRef.current) return;
        if (result.status === "ok") {
          setMessage(result.message);
          setStatus("ok");
        } else {
          setStatus(result.status);
        }
      } catch (e) {
        if (requestId !== requestIdRef.current) return;
        setError(e instanceof Error ? e.message : "Failed to load message");
        setStatus("error");
      }
    })();
  }, [baseUrl, namespaceId, messageId]);

  return { status, message, error };
}
